/* components/ui/Popover.jsx — лёгкий поповер/меню с якорем.
   Позиционируется относительно триггера (bottom-start по умолчанию),
   закрывается по клику-вне и ESC. Не перекрывает навигацию (z-popover).
   Используется для меню «…» в строках, user-menu, фильтров. */

import React from 'react';
import ReactDOM from 'react-dom';
import { Icon } from './Icon.jsx';
import { getOverlayRoot } from '../../lib/overlay.jsx';

  const { useState, useRef, useEffect, useLayoutEffect, cloneElement } = React;

  function Popover({ trigger, children, placement = 'bottom-start', align, gap = 6, width }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);
    const panelRef = useRef(null);

    const place = () => {
      const t = triggerRef.current; if (!t) return;
      const r = t.getBoundingClientRect();
      const pw = panelRef.current ? panelRef.current.offsetWidth : (width || 220);
      const ph = panelRef.current ? panelRef.current.offsetHeight : 160;
      let top, left;
      const [side, al] = placement.split('-');
      if (side === 'bottom') top = r.bottom + gap;
      else if (side === 'top') top = r.top - ph - gap;
      else top = r.bottom + gap;
      if (al === 'end') left = r.right - pw;
      else left = r.left;
      // клампим в вьюпорт
      left = Math.max(8, Math.min(left, window.innerWidth - pw - 8));
      if (top + ph > window.innerHeight - 8) top = Math.max(8, r.top - ph - gap);
      setPos({ top, left });
    };

    useLayoutEffect(() => { if (open) place(); /* eslint-disable-next-line */ }, [open]);

    // menuitem'ы панели (для клавиатурной навигации стрелками)
    const items = () => panelRef.current
      ? Array.from(panelRef.current.querySelectorAll('[role="menuitem"],button:not([disabled])')).filter((el) => el.offsetParent !== null)
      : [];

    // при открытии — фокус на первый пункт; при закрытии — возврат на триггер
    useEffect(() => {
      if (!open) return;
      const id = setTimeout(() => { const it = items(); if (it[0]) it[0].focus(); }, 0);
      return () => clearTimeout(id);
    }, [open]);

    const closeAndRestore = () => {
      setOpen(false);
      const t = triggerRef.current;
      if (t && t.focus) setTimeout(() => { try { t.focus(); } catch (e) {} }, 0);
    };

    useEffect(() => {
      if (!open) return;
      const onDoc = (e) => {
        if (panelRef.current && panelRef.current.contains(e.target)) return;
        if (triggerRef.current && triggerRef.current.contains(e.target)) return;
        setOpen(false);
      };
      const onKey = (e) => {
        if (e.key === 'Escape') { closeAndRestore(); return; }
        const it = items();
        if (!it.length) return;
        const cur = it.indexOf(document.activeElement);
        if (e.key === 'ArrowDown') { e.preventDefault(); it[(cur + 1 + it.length) % it.length].focus(); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); it[(cur - 1 + it.length) % it.length].focus(); }
        else if (e.key === 'Home') { e.preventDefault(); it[0].focus(); }
        else if (e.key === 'End') { e.preventDefault(); it[it.length - 1].focus(); }
      };
      const onScroll = () => place();
      document.addEventListener('mousedown', onDoc);
      document.addEventListener('keydown', onKey);
      window.addEventListener('resize', onScroll, true);
      window.addEventListener('scroll', onScroll, true);
      return () => {
        document.removeEventListener('mousedown', onDoc);
        document.removeEventListener('keydown', onKey);
        window.removeEventListener('resize', onScroll, true);
        window.removeEventListener('scroll', onScroll, true);
      };
    }, [open]);

    const triggerEl = cloneElement(trigger, {
      ref: triggerRef,
      onClick: (e) => { trigger.props.onClick && trigger.props.onClick(e); setOpen((o) => !o); },
      'aria-expanded': open,
      'aria-haspopup': 'menu',
    });

    return (
      <>
        {triggerEl}
        {open && ReactDOM.createPortal(
          <div className="popover" ref={panelRef} role="menu"
               style={{ top: pos.top, left: pos.left, width, zIndex: 'var(--z-popover)' }}>
            {typeof children === 'function' ? children({ close: () => setOpen(false) }) : children}
          </div>,
          getOverlayRoot()
        )}
      </>
    );
  }

  // удобный пункт меню
  function PopoverItem({ icon, children, onClick, danger, close }) {
    return (
      <button className={'popover__item' + (danger ? ' popover__item--danger' : '')} role="menuitem"
              onClick={(e) => { onClick && onClick(e); close && close(); }}>
        {icon && <Icon name={icon} size={16} />}
        <span className="grow">{children}</span>
      </button>
    );
  }

export { Popover };
export { PopoverItem };
